import React, { useEffect, useState } from 'react'
import DayPicker, { DayModifiers } from 'react-day-picker'
import { Button, Paper, Select, Spinner, Typography } from '@telavita-core/react-design-kit'
import {
  endOfDay,
  endOfWeek,
  format,
  formatISO,
  isSameDay,
  isWithinInterval,
  startOfDay,
  startOfWeek,
  subDays
} from 'date-fns'

import { Reservation } from 'models/Reservation'
import { WEEKDAYS_SHORT, WEEKDAYS_LONG, MONTHS } from 'settings/dayPicker'
import { useGetProfessionalAvailabilities, useUpdateReservation } from 'hooks'
import { formatDateTime, formatSelectOptions, handleSelectHour } from 'utils/reservationHandlers'

import { ReactComponent as CloseModalIcon } from 'static/svg/close-modal-desktop.svg'

import * as S from './styles'
import { useGetProfessionalProfile } from 'views/NpsSchedule/hooks/useGetProfessionalProfile'

interface RescheduleContentProps {
  reservation: Reservation
  handleSuccess: (newReservationDate: string) => void
}

interface OptionData {
  id: string | number
  label: string
}

const noAvailabilityMessage = ['Nenhum horário.', <br />, 'Selecione outra data.']

export const RescheduleContent: React.FC<RescheduleContentProps> = ({ reservation, handleSuccess }) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedHour, setSelectedHour] = useState<OptionData>(null)
  const [availabilityHours, setAvailabilityHours] = useState([])
  const [showHourDropdown, setShowHourDropdown] = useState(false)
  const [shouldRefetchData, setShouldRefetchData] = useState(false)

  const {
    uuid,
    startDate,
    patientUsername,
    professionalUsername,
  } = reservation

  const formattedRestriction = [
    formatISO(startOfDay(selectedDate || startDate)),
    formatISO(endOfDay(selectedDate || startDate)),
  ]

  const updateReservation = useUpdateReservation()
  const {
    professionalProfile,
  } = useGetProfessionalProfile(patientUsername)

  const { data, isFetching, refetch } = useGetProfessionalAvailabilities({
    restrictions: formattedRestriction,
    patientUsername,
    professionalUsername,
    productCode: professionalProfile?.productCode
  })

  function handleFormatNewReservationDate() {
    if (!selectedDate || !selectedHour) return

    const formattedDate = formatDateTime(selectedDate, selectedHour)
    const startDate = new Date(formattedDate)

    return formatISO(startDate)
  }

  // Efeito para atualizar o dropdown de horários disponíveis do profissional
  useEffect(() => {
    if (data) {
      const newData = data[0]

      setAvailabilityHours(Object.values(newData)[0])
      setShouldRefetchData(false)
    }
  }, [data])

  // Efeito para buscar os horários disponíveis do profissional quando uma data é selecionada no calendário
  useEffect(() => {
    if (shouldRefetchData) {
      refetch({
        exact: true,
      })
    }
  }, [refetch, shouldRefetchData])

  function handleSelectDateOnCalendar(day: Date, modifiers: DayModifiers) {
    if (modifiers.disabled) return

    updateReservation.reset()
    setSelectedHour(null)
    setShowHourDropdown(true)
    setSelectedDate(day)
    setShouldRefetchData(true)
  }

  function handleCloseHoursDropdown() {
    setShowHourDropdown(false)
    setSelectedHour(null)
    setShouldRefetchData(false)
  }

  function handleUpdateReservation() {
    updateReservation.mutate({
      uuid,
      patientUsername,
      professionalUsername,
      nonRecurrentReservationDates: [{
        oldDate: startDate,
        newDate: handleFormatNewReservationDate()
      }]
    })
  }

  function getDisabledDays() {
    const minDate = startOfWeek(startDate, { weekStartsOn: 0 })
    const maxDate = endOfWeek(startDate, { weekStartsOn: 0 })

    const today = new Date()
    const isTodayWithinWeek = isWithinInterval(today, { start: minDate, end: maxDate })
    const oneDayBeforeStart = subDays(startDate, 1)
    const isTodayTheDayBeforeStartReservation = isSameDay(today, oneDayBeforeStart)

    // Verifica se o dia atual é o dia anterior ao dia da reserva,
    // se for, o calendário só vai mostrar para remarcação os dias disponíveis a partir do dia da reserva.
    if (isTodayTheDayBeforeStartReservation) {
      return [
        { before: startDate },
        { after: maxDate },
      ]
    }

    // Verifica se o dia atual está dentro da semana da reserva,
    // se estiver, o calendário só vai mostrar para remarcação os dias disponíveis a partir do dia atual.
    if (isTodayWithinWeek) {
      return [
        { before: startOfDay(today) },
        { after: maxDate },
      ]
    }

    // Se o dia atual não estiver dentro da semana da reserva e não for o dia anterior ao dia da reserva,
    // o calendário vai mostrar a semana da reserva com os dias disponíveis.
    return [
      { before: minDate },
      { after: maxDate },
    ]
  }

  if (updateReservation.isSuccess) {
    handleSuccess(handleFormatNewReservationDate())
  }

  return (
    <S.Container>
      <S.TextWrapper>
        <Typography variant="content2" center>
          <span>Importante:</span> só é possível remarcar para um dia da mesma semana.
        </Typography>
      </S.TextWrapper>

      <S.CalendarWrapper>
        <DayPicker
          months={MONTHS}
          weekdaysLong={WEEKDAYS_LONG}
          weekdaysShort={WEEKDAYS_SHORT}
          selectedDays={selectedDate}
          initialMonth={startDate}
          disabledDays={getDisabledDays()}
          onDayClick={(day, modifiers) => handleSelectDateOnCalendar(day, modifiers)}
          locale='pt-br'
        />
      </S.CalendarWrapper>

      {updateReservation.error && (
        <Paper variant='danger' customClassName='errorMessageWrapper'>
          <Typography variant="content2" center>
            Não foi possível remarcar a reserva. <br />
            Tente novamente mais tarde. <br />
            Ou escolha outro dia/horário.
          </Typography>
        </Paper>
      )}

      {showHourDropdown && !updateReservation.error &&(
        <S.SelectedNewDateContainer>
          <S.SelectDateWrapper>
            <Typography color='primary' variant="content2" center weight='bold'>
              {format(selectedDate, 'dd/MM')} às
            </Typography>
            <Select
              options={formatSelectOptions(availabilityHours, noAvailabilityMessage) as OptionData[]}
              selected={selectedHour}
              onSelect={(hour) => setSelectedHour(handleSelectHour(hour))}
              label=''
              customClassName='AvailabilitySelect'
              placeholder={isFetching ? 'Carregando...' : 'Selecione'}
            />
            <CloseModalIcon onClick={handleCloseHoursDropdown} role='button'/>

          </S.SelectDateWrapper>
          <Typography variant='content3' center>
            *Brasil (UTC-3) - Horário de Brasília; <br />
            padrão 00h00 a 23h59
          </Typography>
        </S.SelectedNewDateContainer>
      )}

      <Button
        onClick={handleUpdateReservation}
        variant='contained'
        disabled={!selectedHour || updateReservation.isLoading || !!updateReservation.error}
      >
        {updateReservation.isLoading
          ? <Spinner customClassName='customSpinner'/>
          : 'Confirmar remarcação'
        }
      </Button>
    </S.Container>
  )
}
